// KPI paket önbelleği — Zustand. Ayrı dosya: kpi-store.ts 600 satır sınırına yakın.
// Yalnız paket listesi + membership (metricId → paket kimlikleri) tutulur; pencere/diyalog durumu
// bileşen yerelidir, store'a girmez. 60 sn bayatlık önbelleği; yazımlardan sonra `invalidatePackages`.

import { create } from "zustand";
import type { KpiPackage } from "@mahir/shared";
import { listKpiPackageMembership, listKpiPackages } from "../api/kpi";

export const PACKAGES_STALE_MS = 60_000;

interface KpiPackagesState {
  packages: KpiPackage[];
  packageMembership: Record<string, string[]>;
  packagesLoadedAt: number | null;
  packagesLoading: boolean;
  packagesError: string | null;
  loadPackages: (force?: boolean) => Promise<void>;
  invalidatePackages: () => void;
}

// Uçuştaki yükleme sırasında gelen `force` / `invalidatePackages` düşmez: `force` bir tur daha
// koşturur (aynı söz bekletilir), geçersizleme kuşağı ilerletir — uçuştaki sonuç taze sayılmaz.
let inflight: Promise<void> | null = null;
let rerunRequested = false;
let generation = 0;

export const useKpiPackagesStore = create<KpiPackagesState>((set, get) => ({
  packages: [],
  packageMembership: {},
  packagesLoadedAt: null,
  packagesLoading: false,
  packagesError: null,
  loadPackages(force = false) {
    if (inflight) {
      if (force) rerunRequested = true;
      return inflight;
    }
    const { packagesLoadedAt } = get();
    if (!force && packagesLoadedAt !== null && Date.now() - packagesLoadedAt < PACKAGES_STALE_MS) {
      return Promise.resolve();
    }
    inflight = (async () => {
      try {
        do {
          rerunRequested = false;
          const startGen = generation;
          set({ packagesLoading: true, packagesError: null });
          try {
            const [packages, packageMembership] = await Promise.all([
              listKpiPackages(),
              listKpiPackageMembership(),
            ]);
            set({
              packages,
              packageMembership,
              packagesLoadedAt: startGen === generation ? Date.now() : null,
              packagesLoading: false,
            });
          } catch (e) {
            set({
              packagesLoading: false,
              packagesError: e instanceof Error ? e.message : String(e),
            });
          }
        } while (rerunRequested);
      } finally {
        inflight = null;
      }
    })();
    return inflight;
  },
  invalidatePackages() {
    generation += 1;
    set({ packagesLoadedAt: null });
  },
}));
